import React, { useCallback, useEffect, useState } from 'react';
import MainLayout from 'src/components/layouts/MainLayout';
import styles from 'src/styles/layout.module.css';
import { Breadcrumb, Button, Form, Input, Space, Tag, Typography } from 'antd';
import { Link, useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { UserOutlined } from '@ant-design/icons';
import tileStyles from 'src/styles/tile.module.css';

import { useForm } from 'antd/es/form/Form';
import { AppDispatch } from 'src/index';
import { useAccountsState, useAccountsValues } from 'src/features/companyAccounts/companyAccounts.hooks';
import { createAccount, fetchAccounts, updateAccount } from 'src/features/companyAccounts/companyAccounts.state';
import { FormattedMessage, useIntl } from 'react-intl';
import { CompanyAccountRole } from 'src/features/companyAccounts/companyAccounts.types';
import { companyAccountRoleLocaleId } from 'src/locales/helpers';
import { useDebounce } from 'react-use';
import Loader from 'src/components/atoms/Loader';

const emptyAccounts = { email: '', password: '' };

const CompanyAccountsPage: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const state = useAccountsState();
  const companyAccounts = useAccountsValues();
  const history = useHistory();
  const intl = useIntl();
  const [form] = useForm();
  const [search, setSearch] = useState<string | undefined>(undefined);

  const [createMode, setCreateMode] = useState<boolean>(false);
  const [newEntity, setNewEntity] = useState<{
    email: string;
    password: string;
  }>(emptyAccounts);

  const handleSearch = useCallback(async () => {
    if (search !== undefined) {
      dispatch(fetchAccounts({ name: search, email: search }));
    }
  }, [dispatch, search]);
  const handleCreateButtonClick = useCallback(() => {
    setCreateMode(true);
  }, []);

  const handleFormSubmit = useCallback(async () => {
    const response = await dispatch(createAccount(newEntity));

    if (createAccount.fulfilled.match(response)) {
      dispatch(
        updateAccount({
          accountId: response.payload.id,
          data: { fullname: newEntity.email.split('@')[0] },
        })
      );
      history.push(`/commercial/accounts/${response.payload.id}`);
      setCreateMode(false);
      setNewEntity({ email: '', password: '' });
    }
  }, [dispatch, history, newEntity]);

  useEffect(() => {
    dispatch(fetchAccounts({}));
  }, [dispatch]);

  useDebounce(handleSearch, 400, [search]);

  return (
    <MainLayout>
      <div className={styles.mainGrid}>
        <div className={styles.breadCrumbs}>
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to='/commercial/accounts'>
                <Space>
                  <UserOutlined />
                  <FormattedMessage id={'view.company.accounts.title'} />
                </Space>
              </Link>
            </Breadcrumb.Item>
          </Breadcrumb>
        </div>
        <div className={styles.contentArea}>
          <Typography.Title level={2} className={styles.title}>
            <FormattedMessage id={'view.company.accounts.title'} />
          </Typography.Title>
          <div className={styles.searchWrap}>
            <Input
              type='text'
              value={search}
              placeholder='Введите имя или почту'
              style={{ marginRight: 8 }}
              onChange={e => setSearch(e.target.value)}
              autoFocus
            />
          </div>
          {state.isPending ? (
            <Loader />
          ) : (
            <div style={{ marginTop: '24px' }}>
              {createMode && (
                <Form
                  form={form}
                  validateTrigger='onBlur'
                  layout='vertical'
                  className={tileStyles.listTile}
                  onFinish={handleFormSubmit}
                >
                  <UserOutlined />
                  <Form.Item name='email' noStyle rules={[{ required: true, type: 'email' }]}>
                    <Input
                      autoFocus
                      type='text'
                      name='email'
                      placeholder={intl.formatMessage({
                        id: 'view.company.accounts.input.form.createAccount.email.placeholder',
                      })}
                      style={{ width: '35%' }}
                      onChange={ev => setNewEntity(e => ({ ...e, email: ev.target.value }))}
                    />
                  </Form.Item>
                  <Form.Item name='password' noStyle rules={[{ required: true }]}>
                    <Input
                      type='password'
                      name='password'
                      placeholder={intl.formatMessage({
                        id: 'view.company.accounts.input.form.createAccount.password.placeholder',
                      })}
                      style={{ width: '35%' }}
                      onChange={ev => setNewEntity(e => ({ ...e, password: ev.target.value }))}
                    />
                  </Form.Item>
                  <Space direction={'horizontal'}>
                    <Button htmlType='button' type='default' onClick={() => setCreateMode(false)}>
                      <FormattedMessage id={'action.cancel'} />
                    </Button>
                    <Button
                      htmlType='submit'
                      type='primary'
                      disabled={newEntity.email === '' || newEntity.password === ''}
                    >
                      <FormattedMessage id={'action.create'} />
                    </Button>
                  </Space>
                </Form>
              )}
              {companyAccounts.map(c => (
                <div key={c.id} className={tileStyles.listTile}>
                  <Link to={`/commercial/accounts/${c.id}`} className={tileStyles.tileName}>
                    <Space>
                      <UserOutlined />
                      <span>
                        {c.fullname} ({c.email})
                      </span>
                    </Space>
                  </Link>
                  <Space align='center'>
                    <Tag color={c.role === CompanyAccountRole.admin ? 'green' : ''}>
                      <FormattedMessage id={companyAccountRoleLocaleId(c.role)} />
                    </Tag>
                    {c.disabled && <Tag color='red'>Заблокирован</Tag>}
                    {!c.isActive && <Tag color='red'>Не активирован</Tag>}
                  </Space>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className={styles.sidebar}>
          <Space>
            <Button type='primary' onClick={handleCreateButtonClick}>
              <FormattedMessage id={'view.company.accounts.action.create'} />
            </Button>
          </Space>
        </div>
      </div>
    </MainLayout>
  );
};

export default CompanyAccountsPage;
